import { Icon } from "@iconify/react";
import Classnames from "classnames";
import React, { useEffect, useRef } from "react";

import Button from "@shared/button";
import i18n from "@utils/i18n";

import style from "./modal.styl";

type ModalProps = {
  /**
   * function to call on action button click
   */
  action?: () => void;
  /**
   * Disabled action button
   */
  actionDisabled?: boolean;
  /**
   * label for action button
   */
  actionLabel?: string;
  /**
   * label for cancel button
   */
  cancelLabel?: string;
  /**
   * Modal open state
   */
  open?: boolean;
  /**
   * Call this function from within modal to close
   */
  close?: () => void;
  /**
   * Modal Title
   */
  title?: React.ReactNode | string;
  /**
   * Modal content
   */
  children?: React.ReactNode;
  /**
   * show buttons in pending/loading state
   */
  pending?: boolean;
  actionType?: "primary" | "secondary" | "tertiary" | "danger";
  size?: "small" | "medium" | "large" | "xLarge";
  "data-testid"?: string;
};

const Modal = ({
  action,
  open,
  close,
  actionDisabled,
  actionLabel,
  cancelLabel,
  title,
  children,
  pending,
  actionType,
  size = "medium",
  "data-testid": dataTestId = "modal",
}: ModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    // make sure closing by pressing 'esc' updates the parent state properly.
    const handleClose = () => close?.();
    const dialog = dialogRef.current;
    dialog?.addEventListener("close", handleClose);

    return () => {
      dialog?.removeEventListener("close", handleClose);
    };
  }, [close]);

  useEffect(() => {
    if (open) {
      // open modal
      dialogRef.current?.showModal?.();
    } else {
      dialogRef.current?.close?.();
    }
  }, [open]);

  const renderTitle = () => {
    return typeof title === "string" ? (
      <span className="subtitle dark extraStrong">{title}</span>
    ) : (
      title
    );
  };

  const renderContent = () => {
    return children;
  };

  const renderActions = () => {
    return (
      <>
        <Button
          type="secondary"
          size="medium"
          onClick={close}
          disabled={pending}
          data-testid={`button-${dataTestId}-cancel`}
        >
          {cancelLabel ?? i18n.t("common:cancel")}
        </Button>
        <Button
          size="medium"
          onClick={action}
          loading={pending}
          disabled={actionDisabled}
          type={actionType}
          data-testid={`button-${dataTestId}-primary-action`}
        >
          {actionLabel ?? i18n.t("common:ok")}
        </Button>
      </>
    );
  };

  return (
    <dialog
      ref={dialogRef}
      className={Classnames(style.modal, { [style.open]: open })}
      data-testid={dataTestId}
    >
      <Button
        size="tiny"
        transparent
        iconButton
        iconColors
        className={style.close}
        onClick={close}
        data-testid={`button-${dataTestId}-close`}
      >
        <Icon icon="feather:x" />
      </Button>
      <div className={Classnames(style.container, style[size])}>
        <div className={style.title} data-testid={`label-${dataTestId}-title`}>
          {renderTitle()}
        </div>
        <div
          className={style.content}
          data-testid={`label-${dataTestId}-content`}
        >
          {renderContent()}
        </div>
        <div className={style.actions}>{renderActions()}</div>
      </div>
    </dialog>
  );
};

Modal.displayName = "Modal";

export default Modal;
